import { Grid, MenuItem } from '@material-ui/core'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'
import arrayMutators from 'final-form-arrays'
import { makeStyles, Theme } from '@material-ui/core/styles'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { WHITE } from '../../../../theme'
import { ResponsiveContext } from '../../../../utils/context/responsiveContext'
import { isModalVisible } from '../../../reducers/modal/selector'
import { modalTypes } from '../../../../enums/modalTypeEnum'
import { hideModal } from '../../../reducers/modal/actions'
import errorCatcher from '../../../../utils/errorCatcher'
import fetchFactory from '../../../../utils/fetchFactory'
import { lotCompleteCategoryEnum } from '../enums/edlEnum'
import EDLConfirmationModal from './EDLConfirmationModal'
import PopinConfirmationErreurRDV from '../../visites/components/PopinConfirmationErreurRDV'
import CalendrierVisite from '../../visites/components/CalendrierVisite'
import { CalendrierProvider } from '../../visites/context/CalendrierContext'
import Button from '../../../../components/button/Button'
import Loader from '../../../../components/loader/Loader'
import Dialog from '../../../../components/dialog/Dialog'
import { INITIAL_POPIN_CONFIRMATION, INITIAL_POPIN_ERREUR_CONFIRMATION, PopinConfirmationState, PopinErreurConfirmationState } from '../../visites/types/popinConfirmation'
import styles from '../../../../styles'
import { RootState } from '../../../reducers'
import { ClassValue } from 'classnames/types'
import FormSelectField from '../../../../components/fields/FormSelectField'
import selectSizeEnum from '../../../../enums/selectEnums/SelectSizeEnum'
import { objectGetKeys } from '../../../../utils/ieCompatibility'
import { daysEnum } from '../../visites/enums/daysEnum'
import { creneauxHorairesEnum } from '../../visites/enums/creneauxHorairesEnum'
import { configEnum } from '../../../../enums/fetchFactoryEnum'
import { EDLContext } from '../context/EDLContext'
import {RequestItemModel} from '../../../requests/requestsModel'
import {ContractsModel} from '../../../../types/contracts/contractsModel'

const useStyle = makeStyles<Theme, { isLargeScreen: boolean }>((theme: Theme) => ({
			...styles(theme),
			formStyle: {
				marginBottom: 20
			},
			inputStyle: {
				'&:first-child': {
					marginBottom: 60
				},
				'&:nth-child(2)': {
					marginBottom: 20
				},
				'& textarea': {
					paddingTop: 8
				},
			},
			iconStyle: {
				position: 'absolute',
				top: 32,
				right: 32,
				borderRadius: '100%',
				color: theme.palette.blue.main,
				cursor: 'pointer',
				zIndex: 2,
				fontSize: 25,
				padding: 5,
				background: WHITE,

				'&:hover': {
					background: theme.palette.blue['5']
				}
			},
			inputDateStyle: {
				'&:first-child': {
					marginInlineStart: 'auto',
					marginBottom: 60
				},
				'&:nth-child(2)': {
					marginBottom: 20
				},
				'& textarea': {
					paddingTop: 8
				}
			},
			buttonContainerStyle: {
				marginTop: 70
			},
			spinnerStyle: {
				textAlign: 'center'
			},
			chooseOptionStyle: {
				whiteSpace: 'pre-line',
				marginTop: 85,
				marginBottom: 30
			},
			timeSlotContainer: {
				paddingTop: '5%',
				paddingBottom: '5%'
			},
			dateText: {
				fontSize: 18,
				fontWeight: 500,
				color: theme.palette.blue.main,
				opacity: 0.7,
				lineHeight: 1.33
			},
			informationStyle: {
				whiteSpace: 'pre-line',
				marginBottom: 20
			},
			marginBottom: {
				marginTop: 30
			},
			assurance: {
				marginBottom: '32px !important',
				marginLeft: 0,
				width: '100%'
			},
			modalTitle: {
				marginBottom: '32px !important',
			},
			descriptionField: {
				width: '100%'
			},
			labelAssurance: {
				marginRight: '50px',
			},
			sublabel: {
				color: theme.palette.blue['30'],
				marginRight: '-100px'
			},
			sublabelMargin: {
				marginBottom: '8px'
			},
			fieldArrayStyle: {
				paddingBottom: 40
			},
			dialogBody: {
				padding: 16
			},
			bouton: {
				marginTop: '32px'
			},
			selectStyle: {
				...styles(theme).text1,
				fontSize: 15,
				fontWeight: 500,
				color: theme.palette.blue.main,
				lineHeight: 1.33,

				'& .MuiSelect-icon': {
					color: theme.palette.blue.main
				}
			},
			itemStyle: {
				opacity: 1
			},
			calendrierContainer: {
				background: WHITE,
				padding: props => props.isLargeScreen ? '41px 33px 28px 35px' : '23px 25px 21px',
				borderRadius: 30,
				borderBottomRightRadius: 30
			},
			noDispo: {
				cursor: 'pointer'
			},
			boutons: {
				gap: '16px'
			},
			button: {
				minWidth: '150px'
			},
		}
	)
)

interface AcceptingProps {
	onValidate: () => void
	offre: any
	bail?: ContractsModel
	idRequete?: string
	requete?: RequestItemModel
	idVisiteEdl?: string
	isEdle?: boolean
	isEdls?: boolean
	dateEffetBail: Date | null
	dateCongesSouhaitee: Date | null
}

const EDLModal: React.FC<AcceptingProps> = (
	{
		onValidate,
		offre,
		bail,
		requete,
		idRequete = requete?.id,
		isEdle = false,
		isEdls = false,
		idVisiteEdl,
		dateEffetBail,
		dateCongesSouhaitee
	}
	) => {
	const [loading, setLoading] = useState<boolean>(false)
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyle({ isLargeScreen })
	const intl = useIntl()
	const dispatch = useDispatch()
	const edlModalIsOpen = useSelector((state: RootState) => isModalVisible(state, isEdle ? modalTypes.edle : modalTypes.edls))
	const [popinConfirmation, setPopinConfirmation] = useState<PopinConfirmationState>(INITIAL_POPIN_CONFIRMATION)
	const [popinErreurConfirmation, setPopinErreurConfirmation] = useState<PopinErreurConfirmationState>(INITIAL_POPIN_ERREUR_CONFIRMATION)
	const { demandeResidentielId } = useContext(EDLContext)
	const [shouldDisplayAdditionalDates, setShouldDisplayAdditionalDates] = useState<boolean>(false)
	const [shouldDisplayNoDispoMsg, setShouldDisplayNoDispoMsg] = useState<boolean>(true)
	const choices = [1,2,3]

	const AssuranceClass: ClassValue = classNames(
		'row',
		classes.assurance
	)

	const subLabel: ClassValue = classNames(
		classes.text7,
		classes.sublabelMargin
	)

	const onSubmit = (values: any) => {
		setLoading(true)
		let body = values

		if (isEdle && bail?.id) {
			body = { ...body, idBail: bail.id }
		} else if (isEdls && idRequete) {
			body = {...body, idRequete: idRequete}
		}

		fetchFactory(
			isEdle ? '/api/ec_residence/contrat/rdvHorairesPerso' : '/api/ec_residence/rendezvous/rdvHorairesPerso',
			{
				body: JSON.stringify(body),
				method: configEnum.post
			}
		)
			.catch(
				(err) => {
					errorCatcher(err, '')
				}
			)
			.finally(
				() => {
					dispatch(hideModal(isEdle ? modalTypes.edle : modalTypes.edls))
					setLoading(false)
					onValidate()
					window.location.reload()
				}
			)
	}

	const validate = (values: any) => {
		const errors: any = {
			choix1: {jour: undefined, heure: undefined},
			choix2: {jour: undefined, heure: undefined},
			choix3: {jour: undefined, heure: undefined}
		}
		if (!values.choix1.jour) {
			errors.choix1.jour = intl.formatMessage(
				{
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				}
			)
		}
		if (!values.choix1.heure) {
			errors.choix1.heure = intl.formatMessage(
				{
					id: 'global.errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				}
			)
		}
		if (
			(values.choix1.heure === values.choix2.heure && values.choix1.jour === values.choix2.jour && values.choix2.heure !== undefined) ||
			(values.choix1.heure === values.choix3.heure && values.choix1.jour === values.choix3.jour && values.choix3.heure !== undefined)
		) {
			errors.choix1.heure = intl.formatMessage(
				{
					id: 'global.errors.sameCreneau',
					defaultMessage: 'Vous ne pouvez pas choisir deux fois le même créneau',
					description: 'Required message'
				}
			)
			errors.choix1.jour = intl.formatMessage(
				{
					id: 'global.errors.sameCreneau',
					defaultMessage: 'Vous ne pouvez pas choisir deux fois le même créneau',
					description: 'Required message'
				}
			)
		}
		if (values.choix3.heure === values.choix2.heure && values.choix3.jour === values.choix2.jour && values.choix3.heure !== undefined) {
			errors.choix2.heure = intl.formatMessage(
				{
					id: 'global.errors.sameCreneau',
					defaultMessage: 'Vous ne pouvez pas choisir deux fois le même créneau',
					description: 'Required message'
				}
			)
			errors.choix2.jour = intl.formatMessage(
				{
					id: 'global.errors.sameCreneau',
					defaultMessage: 'Vous ne pouvez pas choisir deux fois le même créneau',
					description: 'Required message'
				}
			)
		}
		return errors
	}

	const initialValues = useMemo(() => ({
		choix1: isEdle ? {jour: bail?.jour1 ?? undefined, heure: bail?.heure1 ?? undefined}
			: {jour: requete?.jour1 ?? undefined, heure: requete?.heure1 ?? undefined},
		choix2: isEdle ? {jour: bail?.jour2 ?? undefined, heure: bail?.heure2 ?? undefined}
			: {jour: requete?.jour2 ?? undefined, heure: requete?.heure2 ?? undefined},
		choix3: isEdle ? {jour: bail?.jour3 ?? undefined, heure: bail?.heure3 ?? undefined}
			: {jour: requete?.jour3 ?? undefined, heure: requete?.heure3 ?? undefined},
	}), [bail, isEdle, requete])

	const getDureeEDLE = useCallback(() => {
		if (Number(offre.genre) === lotCompleteCategoryEnum.bedroom) {
			return 45
		} else if (
			Number(offre.genre) === lotCompleteCategoryEnum.studio ||
			Number(offre.genre) === lotCompleteCategoryEnum.t1 ||
			Number(offre.genre) === lotCompleteCategoryEnum.t1Bis ||
			Number(offre.genre) === lotCompleteCategoryEnum.t2 ||
			Number(offre.genre) === lotCompleteCategoryEnum.twoPieces
		) {
			return offre.meuble ? 90 : 45
		} else {
			return offre.meuble ? 90 : 60
		}
	}, [offre])

	return (
		<Dialog
			onCloseDialog={() => {
				dispatch(hideModal(isEdle ? modalTypes.edle : modalTypes.edls))
				setShouldDisplayAdditionalDates(false)
				setShouldDisplayNoDispoMsg(false)
			}}
			open={edlModalIsOpen}
			fullScreen={!isLargeScreen}
			maxWidth={isLargeScreen ? 'md' : undefined}
		>
			<div className={classes.dialogBody}>
				{loading ? (
					<Loader />
					) : (
						<>
							<Form
								mutators={{
									...arrayMutators
								}}
								initialValues={initialValues}
								onSubmit={onSubmit}
								validate={validate}
								render={
									(
										{
											handleSubmit
										}
									) => (
										<form
											onSubmit={handleSubmit}
											className={classes.formStyle}
										>
											<Grid container spacing={2} className={classes.dialogBody}>
												{shouldDisplayAdditionalDates ?
													<>
														<Grid container item justify="flex-end">
															<CloseIcon
																className={classes.iconStyle}
																onClick={() => {
																	dispatch(hideModal(isEdle ? modalTypes.edle : modalTypes.edls))
																	setShouldDisplayAdditionalDates(false)
																	setShouldDisplayNoDispoMsg(false)
																}}
															/>
														</Grid>
														{shouldDisplayNoDispoMsg &&
															<Grid container item justify="flex-end">
																<span className={subLabel}>
																	<FormattedMessage id="preparezArrivee.modalPriseRdvEdle.aucuneDispoTexte" />
																</span>
															</Grid>
														}
														{choices.map(it =>
																<Grid container item direction="row" alignItems="center">
																	<Grid item xs={isLargeScreen ? 4 : 12}>
																		<span className={subLabel}>
																			<FormattedMessage id={`preparezArrivee.modalPriseRdvEdle.choix${it}`} />
																		</span>
																	</Grid>
																	<Grid item xs={isLargeScreen ? 4 : 6}>
																		<div className={AssuranceClass}>
																			<Grid container item xs={12} md={12} direction={isLargeScreen ? 'column' : 'row'}>
																				<Grid item xs={12} md={6}>
																					<Field
																						name={`choix${it}.jour`}
																						component={FormSelectField}
																						size={isLargeScreen ? selectSizeEnum.big : selectSizeEnum.small}
																						inputTextStyle={classes.selectStyle}
																						fullWidth
																					>
																						<MenuItem className={classes.itemStyle} value="" disabled>
																							<FormattedMessage
																								id="preparezArrivee.modalPriseRdvEdle.placeholders.jour"
																								defaultMessage="jour"
																							/>
																						</MenuItem>

																						{objectGetKeys(daysEnum).filter((day) => isNaN(Number(day))).map(
																								(day) =>
																									<MenuItem
																										value={daysEnum[day as keyof typeof daysEnum]}
																										key={day}
																										className={classes.itemStyle}
																									>
																										<FormattedMessage
																											id={`preparezArrivee.modalPriseRdvEdle.enums.jours.${day}`}
																											defaultMessage="vente du logement"
																											description="Search reason item"
																										/>
																									</MenuItem>
																							)
																						}
																					</Field>
																				</Grid>
																			</Grid>
																		</div>
																	</Grid>
																	<Grid item xs={isLargeScreen ? 4 : 6}>
																		<div className={AssuranceClass}>
																			<Grid container item xs={12} md={12} direction={isLargeScreen ? 'column' : 'row'}>
																				<Grid item xs={12} md={6}>
																					<Field
																						name={`choix${it}.heure`}
																						component={FormSelectField}
																						size={isLargeScreen ? selectSizeEnum.big : selectSizeEnum.small}
																						inputTextStyle={classes.selectStyle}
																						fullWidth
																					>
																						<MenuItem className={classes.itemStyle} value="" disabled>
																							<FormattedMessage
																								id="preparezArrivee.modalPriseRdvEdle.placeholders.creneau"
																								defaultMessage="creneau"
																							/>
																						</MenuItem>

																						{
																							objectGetKeys(creneauxHorairesEnum).filter((creneau) => isNaN(Number(creneau))).map(
																								(creneau) =>
																									<MenuItem
																										value={creneauxHorairesEnum[creneau as keyof typeof creneauxHorairesEnum]}
																										key={creneau}
																										className={classes.itemStyle}
																									>
																										<FormattedMessage
																											id={`preparezArrivee.modalPriseRdvEdle.enums.creneaux.${creneau}`}
																											defaultMessage="vente du logement"
																											description="Search reason item"
																										/>
																									</MenuItem>
																							)
																						}
																					</Field>
																				</Grid>
																			</Grid>
																		</div>
																	</Grid>
																</Grid>
															)}
													</>
													:
													<>
														<CalendrierProvider
															offre={offre}
															setPopinConfirmation={setPopinConfirmation}
															popinErreurConfirmation={popinErreurConfirmation}
															setPopinErreurConfirmation={setPopinErreurConfirmation}
															dureeRdv={getDureeEDLE()}
															isEdle={isEdle}
															isEdls={isEdls}
															idVisiteEdl={idVisiteEdl}
															demandeResidentielId={demandeResidentielId}
															minDate={dateEffetBail !== null ? dateEffetBail : undefined}
															maxDate={dateCongesSouhaitee !== null ? dateCongesSouhaitee : undefined}
														>
															{
																popinConfirmation.open && (
																	<EDLConfirmationModal date={popinConfirmation.date} isEdle={isEdle} />
																)
															}
															{
																popinErreurConfirmation.open && (
																	<PopinConfirmationErreurRDV
																		onClose={() => setPopinErreurConfirmation({
																				...popinErreurConfirmation,
																				open: false
																			}
																		)} error_id={popinErreurConfirmation.error_id}
																	/>
																)
															}
															{
																!popinConfirmation.open && !popinErreurConfirmation.open && (
																	<Grid container item xs={12}>
																		<Grid container item xs className={classes.calendrierContainer}>
																			<CalendrierVisite onClose={() => dispatch(hideModal(isEdle ? modalTypes.edle : modalTypes.edls))} />
																		</Grid>
																	</Grid>
																)
															}
														</CalendrierProvider>
													</>
												}
												{!shouldDisplayAdditionalDates && !popinConfirmation.open && !popinErreurConfirmation.open &&
													<Grid container item justify="center">
														<div onClick={() => setShouldDisplayAdditionalDates(true)}>
															<u className={classes.noDispo}>
																<FormattedMessage id="preparezArrivee.modalPriseRdvEdle.aucuneDispo" />
															</u>
														</div>
													</Grid>
												}
												{shouldDisplayAdditionalDates &&
													<Grid container item justify="center" className={classes.boutons}>
														<div className={classes.button}>
															<Button
																background="backgroundWhite"
																color="colorAccessibility"
																textStyle="text8"
																size="bigButton"
																onClick={() => setShouldDisplayAdditionalDates(false)}
															>
																<FormattedMessage
																	id="global.btn.cancel"
																	defaultMessage="Retour"
																	description="cancel button"
																/>
															</Button>
														</div>
														<div className={classes.button}>
															<Button
																background="backgroundAccessibility"
																color="colorWhite"
																textStyle="text8"
																size="bigButton"
															>
																<FormattedMessage
																	id="global.btn.validate"
																	defaultMessage="Valider"
																	description="Finish button"
																/>
															</Button>
														</div>
													</Grid>
												}
											</Grid>
										</form>
									)
								}
							/>
						</>
				)
				}
			</div>
		</Dialog>
	)
}

export default EDLModal
